import { useState, useRef, useEffect } from 'react';
import { cn } from '../lib/cn';

interface EditableCellProps {
  value: string;
  hasError: boolean;
  errors: string[];
  onChange: (newValue: string) => void;
}

export function EditableCell({ value, hasError, errors, onChange }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleBlur = () => {
    setEditing(false);
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setEditing(false);
      if (localValue !== value) onChange(localValue);
    }
    if (e.key === 'Escape') {
      setEditing(false);
      setLocalValue(value);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={localValue}
        onChange={e => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full px-1 py-0.5 text-xs border rounded outline-none',
          hasError ? 'border-red-400 bg-red-50' : 'border-blue-400 bg-white'
        )}
      />
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      title={errors.join('\n')}
      className={cn(
        'px-1 py-0.5 text-xs cursor-pointer rounded min-h-[1.5rem] flex items-center gap-1 group truncate',
        hasError
          ? 'bg-red-50 border border-red-300 text-red-800'
          : 'hover:bg-blue-50 hover:border-blue-200 border border-transparent'
      )}
    >
      <span className="truncate flex-1">{value || <span className="text-gray-400 italic">empty</span>}</span>
      {hasError && (
        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-red-500" />
      )}
    </div>
  );
}
